import { ArrowRight, Shield, CheckCircle, Globe, Banknote, FileText, Video } from 'lucide-react';
import { Link } from 'react-router-dom';
import { useState } from 'react';
import SEO from '../SEO';
import { pageSEO, faqSchemas } from '../../seoData';
import RelatedServices from '../RelatedServices';

const NriUAE = () => {
  const [expandedFaq, setExpandedFaq] = useState(null);

  const faqs = [
    { question: "What are the tax advantages for UAE NRIs investing in India?", answer: "UAE has no income tax, making it advantageous. When you invest in India, you only pay Indian capital gains tax (no double taxation since UAE has no tax). Long-term equity gains over Rs.1L are taxed at 10%, short-term at 15%. We optimize your portfolio for maximum tax efficiency." },
    { question: "Can I repatriate investment returns from India to UAE?", answer: "Yes! Investments made through NRE accounts are fully repatriable - both principal and returns. NRO account repatriation is limited to $1 million per year. We help you structure investments through the right accounts for hassle-free repatriation to your UAE bank account." },
    { question: "What FEMA rules should UAE NRIs know?", answer: "FEMA governs all foreign exchange transactions for NRIs. Key rules: NRIs can invest in equity, mutual funds, and bonds. Real estate investment has specific rules. LRS allows $250,000 outward remittance per year. We ensure all your investments are 100% FEMA compliant." },
    { question: "Do you offer consultations in UAE time zone?", answer: "Absolutely! UAE is only 1.5 hours behind India (IST), making it very convenient. We offer video consultations via Zoom/Google Meet during UAE business hours. WhatsApp support is available for quick queries. Many of our existing NRI clients are based in Dubai, Abu Dhabi, and Sharjah." },
  ];

  const services = [
    { icon: <Banknote className="h-7 w-7 text-white" />, title: "Tax-Free Income Advantage", desc: "UAE has zero income tax. Invest your tax-free UAE earnings in India's high-growth markets. You only pay Indian capital gains tax, maximizing your overall returns." },
    { icon: <Shield className="h-7 w-7 text-white" />, title: "FEMA Compliance", desc: "100% FEMA compliant investment structuring. We ensure all your India investments follow RBI and FEMA regulations for hassle-free investing and repatriation." },
    { icon: <FileText className="h-7 w-7 text-white" />, title: "AED to INR Strategy", desc: "Strategic timing of AED to INR conversion for investments. We monitor exchange rates and advise on optimal conversion timing to maximize your rupee investment." },
    { icon: <Globe className="h-7 w-7 text-white" />, title: "NRE/NRO Management", desc: "Complete NRE and NRO account setup and management. NRE for repatriable investments, NRO for Indian income. We handle documentation and compliance." },
    { icon: <Video className="h-7 w-7 text-white" />, title: "Dubai-Friendly Timings", desc: "UAE is only IST -1:30, making consultations very convenient. Video calls during UAE business hours, WhatsApp support, and quick email responses." },
    { icon: <CheckCircle className="h-7 w-7 text-white" />, title: "Full Repatriation", desc: "NRE investments are 100% repatriable to UAE. We handle all documentation, TDS certificates, and bank formalities for smooth repatriation to your UAE bank." },
  ];

  return (
    <div className="min-h-screen bg-white">
      <SEO {...pageSEO.nriUAE} faqJsonLd={faqSchemas.nriUAE} />

      <section className="relative bg-gradient-to-br from-green-900 via-emerald-800 to-teal-900 pt-20 pb-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-4xl">
            <span className="inline-block bg-red-500 text-white px-4 py-2 rounded-full text-sm font-semibold tracking-wide mb-6">
              UAE & DUBAI NRI SERVICES
            </span>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-white mb-6 leading-tight">
              India Investments for
              <span className="block bg-gradient-to-r from-green-300 to-emerald-300 bg-clip-text text-transparent">
                UAE & Dubai NRIs
              </span>
            </h1>
            <p className="text-lg text-green-200 mb-8 leading-relaxed max-w-3xl">
              Maximize your tax-free UAE earnings by investing in India's booming economy. Rumtek Finvest 
              has been helping UAE and Dubai-based NRIs since 2009 with FEMA-compliant investment strategies, 
              seamless repatriation, and personalized portfolio management. Leverage your zero-tax UAE income 
              to build significant wealth in India.
            </p>
            <div className="flex flex-col sm:flex-row gap-4">
              <Link to="/contact" className="bg-white text-green-900 px-8 py-4 rounded-xl font-bold text-lg hover:bg-gray-100 transition-all transform hover:scale-105 inline-flex items-center justify-center shadow-xl" data-testid="nri-uae-cta">
                Free NRI Consultation <ArrowRight className="ml-2 h-5 w-5" />
              </Link>
              <Link to="/nri-financial-planning" className="border-2 border-white text-white hover:bg-white hover:text-green-900 px-8 py-4 rounded-xl font-bold text-lg transition-all inline-flex items-center justify-center">
                General NRI Services
              </Link>
            </div>
          </div>
        </div>
      </section>

      <section className="py-12 bg-white border-b">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-8 text-center">
            <div><div className="text-3xl font-bold text-green-700 mb-1">0% Tax</div><div className="text-gray-600 text-sm">UAE Income Tax</div></div>
            <div><div className="text-3xl font-bold text-green-700 mb-1">Since 2009</div><div className="text-gray-600 text-sm">Serving UAE NRIs</div></div>
            <div><div className="text-3xl font-bold text-green-700 mb-1">100%</div><div className="text-gray-600 text-sm">Repatriable via NRE</div></div>
            <div><div className="text-3xl font-bold text-green-700 mb-1">IST -1:30</div><div className="text-gray-600 text-sm">Convenient Timing</div></div>
          </div>
        </div>
      </section>

      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl lg:text-4xl font-bold text-gray-900 mb-4 text-center">Tailored Services for UAE NRIs</h2>
          <p className="text-lg text-gray-600 text-center mb-12 max-w-2xl mx-auto">Investment strategies designed for NRIs in Dubai, Abu Dhabi, Sharjah, and across the UAE</p>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {services.map((item, i) => (
              <div key={i} className="bg-white rounded-2xl p-8 shadow-lg hover:shadow-xl transition-all">
                <div className="w-14 h-14 bg-gradient-to-br from-green-600 to-emerald-700 rounded-2xl flex items-center justify-center mb-5">{item.icon}</div>
                <h3 className="text-xl font-bold text-gray-900 mb-3">{item.title}</h3>
                <p className="text-gray-600 leading-relaxed">{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-20 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl lg:text-4xl font-bold text-gray-900 mb-4 text-center">UAE NRI Tax Advantage</h2>
          <p className="text-lg text-gray-600 text-center mb-10">How zero UAE tax maximizes your India investment returns</p>
          <div className="space-y-4">
            {[
              { title: "Zero Double Taxation", desc: "Unlike UK or US NRIs, UAE NRIs face no double taxation since UAE has no income tax. You only pay Indian capital gains tax, keeping more of your investment returns." },
              { title: "Long-Term Equity: 10% Tax Only", desc: "Hold equity investments for 12+ months and pay just 10% on gains above Rs.1 Lakh. With no UAE tax to worry about, this is one of the most tax-efficient investment options globally." },
              { title: "NRE Interest: Tax-Free in India", desc: "Interest earned on NRE Fixed Deposits is completely tax-free in India. Combined with zero UAE tax, this means truly tax-free returns on your NRE deposits." },
              { title: "Full Repatriation Rights", desc: "NRE account investments (principal + returns) are 100% repatriable. Transfer money back to your UAE bank account anytime without RBI restrictions." },
            ].map((item, i) => (
              <div key={i} className="bg-green-50 rounded-xl p-6 border border-green-100">
                <h3 className="font-bold text-green-900 text-lg mb-2">{item.title}</h3>
                <p className="text-gray-700">{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-20 bg-gray-50">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl lg:text-4xl font-bold text-gray-900 mb-10 text-center">UAE NRI Investment FAQs</h2>
          <div className="space-y-4">
            {faqs.map((faq, i) => (
              <div key={i} className="bg-white rounded-xl shadow-sm overflow-hidden">
                <button onClick={() => setExpandedFaq(expandedFaq === i ? null : i)} className="w-full px-6 py-5 text-left flex justify-between items-center">
                  <span className="font-semibold text-gray-900">{faq.question}</span>
                  <span className="text-2xl text-green-600 ml-4">{expandedFaq === i ? '−' : '+'}</span>
                </button>
                {expandedFaq === i && <div className="px-6 pb-5"><p className="text-gray-700 leading-relaxed">{faq.answer}</p></div>}
              </div>
            ))}
          </div>
        </div>
      </section>

      <RelatedServices links={['nriUK', 'nriUS', 'mutualFund', 'gold', 'fd']} />

      <section className="py-16 bg-gradient-to-r from-green-900 to-emerald-900">
        <div className="max-w-4xl mx-auto px-4 text-center text-white">
          <h2 className="text-3xl font-bold mb-4">Start Investing in India from the UAE</h2>
          <p className="text-lg mb-8 text-green-200">Book a free video consultation - UAE-friendly timings available.</p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/contact" className="bg-white text-green-900 px-8 py-4 rounded-xl font-bold text-lg hover:bg-gray-100 transition-all inline-flex items-center">
              Book Consultation <ArrowRight className="ml-2 h-5 w-5" />
            </Link>
            <a href="https://wa.me/917044055500" target="_blank" rel="noopener noreferrer" className="border-2 border-white text-white px-8 py-4 rounded-xl font-bold text-lg hover:bg-white hover:text-green-900 transition-all inline-flex items-center">
              WhatsApp Us
            </a>
          </div>
        </div>
      </section>
    </div>
  );
};

export default NriUAE;
